/**
 * The client site this workspace belongs to.
 *
 * RUNTIME, NOT BUILD TIME. One image is built per commit and can be deployed to
 * more than one environment, so baking a per-environment origin into the bundle
 * would make the image environment-specific while its tag said otherwise.
 * nginx serves `/runtime-config.js` with the value substituted at container
 * start (`frontend/nginx.conf`), exactly as it already does for the API host.
 *
 * It must be the PUBLIC host, never an internal Docker name — the user's own
 * browser goes there.
 *
 * The `VITE_` fallback is the Vite dev server, where `/runtime-config.js` 404s
 * because there is no nginx in front.
 */
declare global {
  interface Window {
    __SA_CONFIG__?: { clientSiteUrl?: string; googleMapsApiKey?: string };
    /** Populated by the Maps script. `maps` is truthy BEFORE `maps.Map` is
     *  attached — see `google-maps.ts`, this is why the type is optional all
     *  the way down. */
    google?: { maps?: { Map?: unknown; [key: string]: unknown } };
  }
}

/** A runtime value that survived substitution, or undefined. */
function runtimeValue(key: 'clientSiteUrl' | 'googleMapsApiKey'): string | undefined {
  const value = typeof window === 'undefined' ? undefined : window.__SA_CONFIG__?.[key];
  // An unsubstituted placeholder means the container was started without the
  // variable; treated as absent rather than used.
  return value !== undefined && value !== '' && !value.includes('${') ? value : undefined;
}

export const CLIENT_SITE_URL: string = (
  runtimeValue('clientSiteUrl') ??
  (import.meta.env.VITE_CLIENT_SITE_URL as string | undefined) ??
  ''
).replace(/\/+$/, '');

/**
 * The SAME key frontend-client uses (`GOOGLE_MAPS_API_KEY`).
 *
 * One Google Cloud project, one billing line, one set of HTTP-referrer
 * restrictions — which is the part that actually matters: a second key would
 * mean a second place to remember to add `workspace-*.scoutaround.io` to, and
 * the failure is a blank map nobody can explain.
 *
 * Empty is the OFF state and it is honest about it: the map tab says the map is
 * not configured rather than rendering a grey box.
 */
export const GOOGLE_MAPS_API_KEY: string =
  runtimeValue('googleMapsApiKey') ??
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ??
  '';
